/**
 * The tenant's portfolio, one item per programme, each opening to its detail.
 *
 * A programme with four revisions is one item that leads with the revision in
 * force; the rest are its curriculum history in the detail pane.
 *
 * @module components/portfolio/PortfolioProgrammes
 */

import React, { type ReactNode } from "react";

import { curriculumRevisionLabel } from "@/components/shared/revision-labels";
import { lifecycleTone } from "@/components/shared/status-tones";
import {
  type AsyncState,
  Badge,
  EmptyState,
  MasterDetail,
  MasterItem,
  MasterList,
} from "@/components/ui";
import type { PortfolioProgrammeRevision, ProgrammeGovernanceSummary } from "@/platform";

import type { ProgrammeGroup } from "./portfolio-groups";
import { PortfolioProgrammeDetail } from "./PortfolioProgrammeDetail";
import { anchorScope } from "./programme-anchor";
import { LIFECYCLE_LABEL, publicationState, TONE_BADGE } from "./revision-presentation";

export interface PortfolioProgrammesProps {
  groups: readonly ProgrammeGroup[];
  canManage: boolean;
  canPublish: boolean;
  busy?: boolean;
  /** Whether a filter is narrowing the list, which decides the empty state. */
  filtered?: boolean;
  /** The filter row, kept above the list inside the master's surface. */
  filters?: ReactNode;
  selectedId: string | undefined;
  onSelect: (programmeId: string, options?: { replace: boolean }) => void;
  onBack: () => void;
  historyState: (programmeId: string) => AsyncState<ProgrammeGovernanceSummary | null>;
  onRetryHistory: (programmeId: string) => void;
  onManage: (revision: PortfolioProgrammeRevision) => void;
  onCompare: (programmeId: string, revisionId: string) => void;
  onChangeCaseOpen: (programmeId: string, changeCaseId: string) => void;
}

/**
 * @example
 * ```tsx
 * <PortfolioProgrammes groups={groups} selectedId={id} onSelect={open} onBack={close} … />
 * ```
 */
export function PortfolioProgrammes({
  groups,
  canManage,
  canPublish,
  busy = false,
  filtered = false,
  filters = undefined,
  selectedId,
  onSelect,
  onBack,
  historyState,
  onRetryHistory,
  onManage,
  onCompare,
  onChangeCaseOpen,
}: PortfolioProgrammesProps): React.ReactElement {
  const selected = groups.find((group) => group.programmeId === selectedId);
  // A badge every item would repeat says nothing, so each kind shows only
  // where programmes differ on it.
  const lifecycles = new Set(groups.map((group) => anchorScope(group).lifecycleStatus));
  const publications = new Set(groups.map((group) => publicationState(anchorScope(group)).label));

  return (
    <MasterDetail
      label="Programmes in the approved portfolio"
      selected={selected !== undefined}
      onBack={onBack}
      backLabel="Back to programmes"
      autoSelectFirst={{
        firstId: groups[0]?.programmeId,
        onSelect: (programmeId) => {
          onSelect(programmeId, { replace: true });
        },
      }}
      masterHeader={filters}
      master={
        groups.length === 0 ? (
          <div className="p-3">
            <EmptyState
              icon="archive"
              title={
                filtered
                  ? "No approved programmes match these filters"
                  : "No programmes have been approved yet"
              }
              description={
                filtered
                  ? "Clear a filter to see the rest of the portfolio."
                  : "Programmes appear here once they are approved."
              }
              density="row"
              data-testid="portfolio-empty"
            />
          </div>
        ) : (
          <MasterList>
            {groups.map((group) => {
              const { primary } = group;
              const scope = anchorScope(group);
              const publication = publicationState(scope);
              return (
                <MasterItem
                  key={group.programmeId}
                  title={primary.title}
                  meta={`${primary.programmeCode ?? primary.awardType} · ${
                    primary.institutionalLabel
                  } · ${curriculumRevisionLabel(primary.revisionNumber)}`}
                  selected={group.programmeId === selected?.programmeId}
                  onSelect={() => {
                    onSelect(group.programmeId);
                  }}
                  rowTestId={`portfolio-programme-${group.programmeId}`}
                  data-testid={`portfolio-open-${group.programmeId}`}
                >
                  {lifecycles.size > 1 && (
                    <Badge
                      tone={TONE_BADGE[lifecycleTone(scope.lifecycleStatus)]}
                      size="sm"
                      variant="soft"
                    >
                      {LIFECYCLE_LABEL[scope.lifecycleStatus] ?? scope.lifecycleStatus}
                    </Badge>
                  )}
                  {publications.size > 1 && (
                    <Badge tone={publication.tone} size="sm" variant="soft">
                      {publication.label}
                    </Badge>
                  )}
                </MasterItem>
              );
            })}
          </MasterList>
        )
      }
      detail={
        selected ? (
          <PortfolioProgrammeDetail
            group={selected}
            canManage={canManage}
            canPublish={canPublish}
            busy={busy}
            historyState={historyState}
            onRetryHistory={onRetryHistory}
            onManage={onManage}
            onCompare={onCompare}
            onChangeCaseOpen={onChangeCaseOpen}
          />
        ) : (
          <EmptyState icon="archive" title="Select a programme to see its detail" density="pane" />
        )
      }
      data-testid="portfolio-programmes"
    />
  );
}
